import { Button, Text } from "@solomon-labs/design/components/ui";
import React from "react";

import { List, ListItem } from "../../../components/Data/List";
import { ListRail } from "../../../components/ListRail";
import { IndicationUnit } from "../../../components/Metrics/IndicationUnit";
import { RailHeader } from "../../../components/RailHeader";
import { ToggleMenu, ToggleMenuItem } from "../../../components/ui/toggle-menu";

import { framable, type FramableProps } from "./framable";
import {
    NB_A_PROGRAM_NAME,
    NB_A_VIEW_CONFIG,
    NB_A_VIEW_KEY_STATS,
    NB_A_VIEW_NEXT_DISTRIBUTION,
    NB_A_VIEW_PARTICIPANTS,
    NB_A_VIEW_POLICIES,
    NB_A_VIEW_POLICY_VALUES,
    NB_A_VIEW_TAGS,
    NB_ACTIVE_PROGRAMS,
    type NeobankProgramRow
} from "./neobankFixtures";
import { ProgramActiveHeader } from "./ProgramActiveHeader";
import { ProgramConfiguration } from "./ProgramConfiguration";
import { ProgramKeyStats } from "./ProgramKeyStats";
import { ProgramPolicies } from "./ProgramPolicies";
import { ProgramProgress } from "./ProgramProgress";
import { QualifiedUsers } from "./QualifiedUsers";

/** No-op — the settled read view mutates nothing; the create-mode
 *  setters/mutators the reused `ProgramConfiguration` / `ProgramPolicies` props
 *  require are wired to this. */
const NOOP = (): void => {};

/**
 * Neobank scenario — **Outcome: the whole Programs page, both programs Active**.
 *
 * The settled end-state of the two-program build, rendered as the real
 * Programs page's **two-pane master–detail layout** (mirrors
 * `apps/yaas-client/src/pages/Programs/Programs.tsx`: a `flex` row of the
 * {@link ListRail} on the LEFT and the selected program's `ProgramDetail` on
 * the RIGHT, `flex-1`, divided by the rail's `border-r`).
 *
 *  - **Left rail** — the same {@link ListRail} + {@link RailHeader} +
 *    status-filter ToggleMenu + program rows the real page composes, showing
 *    BOTH neobank programs ("Continuous Balance Rewards" and "Deposit
 *    Campaign") each with a green Active status dot. The "Active" filter tab is
 *    pre-selected and the first row ("Continuous Balance Rewards") reads
 *    selected — exactly as the app's rail would render after both go live.
 *  - **Right detail** — the SELECTED program's (Continuous Balance Rewards)
 *    active read view: the same composition {@link ProgramViewSim} renders
 *    (`ProgramActiveHeader` + `ProgramKeyStats` + `ProgramProgress` +
 *    read-only `ProgramConfiguration` + read-only `ProgramPolicies` +
 *    `QualifiedUsers`) but SETTLED — no cursor tour, a static outcome frame.
 *    Continuous → Variable/earned budget → no progress meters
 *    (`ProgramProgress` renders nothing).
 *
 * A settled frame — no cursor, no playthrough; just the outcome both scenarios
 * land on. Self-contained (fixtures only — {@link NB_ACTIVE_PROGRAMS} for the
 * rail, the `NB_A_VIEW_*` block for the Continuous read view) so it renders
 * with no wallet / RPC / orgApiFetch / cursor.
 */
/** No sim-specific props — the settled outcome frame is static (no loop). The
 *  `framed` capability comes from {@link FramableProps}. */
export type NeobankProgramsOutcomeSimProps = FramableProps;

function NeobankProgramsOutcomeSimView(): React.ReactElement {
    return (
        // Mirrors Programs.tsx's `<div className="flex flex-1 min-h-0">` —
        // the rail (left, its own `border-r`) then the detail (right, flex-1).
        // Named container so the rail narrows on the FRAME's width (the sim is
        // framed — viewport breakpoints can't see it), matching the app's
        // viewport-driven rail shrink.
        <div className="@container/pane flex min-h-0">
            <ProgramsOutcomeRail
                rows={NB_ACTIVE_PROGRAMS}
                selectedFilter="active"
                selectedRowId={NB_ACTIVE_PROGRAMS[0]?.id ?? ""}
                programCountLabel={`${NB_ACTIVE_PROGRAMS.length} programs`}
            />
            <NeobankContinuousDetailView />
        </div>
    );
}

/**
 * Settled Programs-page outcome (both programs Active). Add `framed` to wrap it
 * in its own fixed-height {@link SimFrame} (the block owns its max height); omit
 * it to render bare.
 */
export const NeobankProgramsOutcomeSim = framable(
    NeobankProgramsOutcomeSimView,
    "NeobankProgramsOutcomeSim"
);

/**
 * The RIGHT detail pane — the selected "Continuous Balance Rewards" program's
 * settled active read view. Same composition as {@link ProgramViewSim} (the
 * `ProgramActiveHeader` + `ProgramKeyStats` + `ProgramProgress` + read-only
 * `ProgramConfiguration` + read-only `ProgramPolicies` + `QualifiedUsers`),
 * seeded from the Continuous program's `NB_A_VIEW_*` fixtures — but with NO
 * cursor tour and NO sim script (this is a static outcome frame, not a
 * playthrough). Continuous / Variable-earned → both progress meters are `null`
 * so `ProgramProgress` renders nothing. `flex-1` so it fills the width left of
 * the rail, matching the real page's `ProgramDetail`.
 *
 * Exported so the mobile master-detail sim ({@link NeobankProgramsMobileSim})
 * can reuse the exact same Continuous read view for its detail screen.
 */
export function NeobankContinuousDetailView(): React.ReactElement {
    return (
        <div className="flex-1 overflow-auto">
            {/* Active-title header — title + StatusBadge "Active" + tag chips,
                with the live Next Distribution countdown meter on the right. */}
            <ProgramActiveHeader
                name={NB_A_PROGRAM_NAME}
                statusLabel="Active"
                statusState="success"
                statusLabelClassName="text-success-base"
                tags={NB_A_VIEW_TAGS}
                nextDistribution={NB_A_VIEW_NEXT_DISTRIBUTION}
            />

            {/* Key-stats row — held / accrued / projected / distributed /
                users. */}
            <ProgramKeyStats stats={NB_A_VIEW_KEY_STATS} />

            {/* Progress meters — Continuous / Variable-earned → both null →
                ProgramProgress renders nothing (no budget or timeline meter). */}
            <ProgramProgress budgetMeter={null} timelineMeter={null} />

            {/* Read-only Configuration summary — `status: "active"` → the
                `!creation` read-summary path; the draft setters are no-ops. */}
            <ProgramConfiguration
                program={NB_A_VIEW_CONFIG}
                programType={NB_A_VIEW_CONFIG.programType ?? "continuous"}
                setProgramType={NOOP}
                budgetMode={NB_A_VIEW_CONFIG.budgetMode ?? "open"}
                setBudgetMode={NOOP}
                apyMode={
                    (NB_A_VIEW_CONFIG.baseApy ?? 0) > 0 ? "base" : "earned"
                }
                setApyMode={NOOP}
                baseApy={NB_A_VIEW_CONFIG.baseApy ?? 0}
                setBaseApy={NOOP}
                userPct={NB_A_VIEW_CONFIG.userPct ?? 80}
                setUserPct={NOOP}
                periodDays={NB_A_VIEW_CONFIG.periodDays ?? 0}
                setPeriodDays={NOOP}
                settlementCycleLength={
                    NB_A_VIEW_CONFIG.settlementCycleLength ?? 0
                }
                setSettlementCycleLength={NOOP}
                settlementCycleUnit={
                    NB_A_VIEW_CONFIG.settlementCycleUnit ?? "days"
                }
                setSettlementCycleUnit={NOOP}
                budget={NB_A_VIEW_CONFIG.budget ?? 0}
                setBudget={NOOP}
            />

            {/* Read-only Policies list — `status: "active"` → the read path;
                the mutators are no-ops, no cursor `demo`. */}
            <ProgramPolicies
                program={{ status: "active" }}
                policies={NB_A_VIEW_POLICIES}
                policyValues={NB_A_VIEW_POLICY_VALUES}
                onAddPolicy={NOOP}
                onRemovePolicy={NOOP}
                onChangePolicyValue={NOOP}
            />

            {/* Qualified Users table — no cursor `demo`. */}
            <QualifiedUsers participants={NB_A_VIEW_PARTICIPANTS} />
        </div>
    );
}

/** The status the outcome rail paints on a row's dot — Pending (amber) while a
 *  just-added program is confirming, Active (green) once it goes live. */
export type OutcomeRowStatus = "pending" | "active";

/** A row for the outcome rail. Extends the fixture row shape with the live
 *  activation status the single-program tail sims animate (Pending → Active).
 *  Fixture rows (both Active) omit `rowStatus` → treated as "active". */
export interface OutcomeRailRow extends NeobankProgramRow {
    rowStatus?: OutcomeRowStatus;
}

export interface ProgramsOutcomeRailProps {
    /** The program rows to render in the rail. */
    rows: OutcomeRailRow[];
    /** Which status-filter tab reads selected. Defaults to "active". */
    selectedFilter?: string;
    /** Which row reads selected (single-select highlight). */
    selectedRowId?: string;
    /** Copy for the header count slot (e.g. "1 program", "2 programs"). */
    programCountLabel: string;
}

/**
 * ProgramsOutcomeRail — the shared Programs rail composition the Neobank
 * outcome + the per-scenario activation tails land on. The same
 * {@link ListRail} + {@link RailHeader} + status-filter ToggleMenu + program
 * rows the real Programs page composes, driven by fixture rows (no wallet / RPC
 * / orgApiFetch). Each row's dot reflects its `rowStatus` (Pending amber →
 * Active green) so a single-program tail can animate the just-created program
 * activating.
 */
export function ProgramsOutcomeRail({
    rows,
    selectedFilter = "active",
    selectedRowId,
    programCountLabel
}: ProgramsOutcomeRailProps): React.ReactElement {
    return (
        <ListRail
            // Narrows on the pane's width (< @3xl/pane = 768px) then restores
            // the default 320px, mirroring the app's `w-52 md:w-80` rail.
            className="w-52 @3xl/pane:w-80"
            header={
                <RailHeader
                    // Narrow rail (< @2xs/rail = 288px): stack the count over
                    // the button; inline row when wider. Mirrors the app rail.
                    className="flex-col items-start justify-start gap-2 @2xs/rail:flex-row @2xs/rail:items-center @2xs/rail:justify-between @2xs/rail:gap-4"
                    count={
                        <Text variant="small" className="text-foreground-muted">
                            {programCountLabel}
                        </Text>
                    }
                    action={
                        <Button variant="raised" interaction="lifted" size="xs">
                            +New Program
                        </Button>
                    }
                />
            }
            filter={
                // Full-width + wrap so the status tabs flow onto a second line
                // when the rail is too narrow to hold them in one row.
                <ToggleMenu
                    className="w-full flex-wrap"
                    value={selectedFilter}
                    onChange={() => {}}
                    spacing={1}
                >
                    <ToggleMenuItem value="all" variant="toggle" size="sm">
                        All
                    </ToggleMenuItem>
                    <ToggleMenuItem value="active" variant="toggle" size="sm">
                        Active
                    </ToggleMenuItem>
                    <ToggleMenuItem value="draft" variant="toggle" size="sm">
                        Draft
                    </ToggleMenuItem>
                </ToggleMenu>
            }
        >
            <List
                selectable="single"
                selected={selectedRowId ?? rows[0]?.id ?? ""}
                onSelect={() => {}}
            >
                {rows.map((program) => (
                    <OutcomeProgramRow key={program.id} program={program} />
                ))}
            </List>
        </ListRail>
    );
}

/** One program row in the outcome rail — a dependency-free equivalent of the
 *  app's `ProgramRow` (status dot + name on the top line, region on the
 *  sub-line). The dot is amber while `rowStatus === "pending"`, green once
 *  Active (the default for fixture rows, which carry no `rowStatus`). */
function OutcomeProgramRow({
    program
}: {
    program: OutcomeRailRow;
}): React.ReactElement {
    const pending = program.rowStatus === "pending";
    return (
        <ListItem itemKey={program.id} padding="px-4 py-3" gap="gap-0">
            <div className="min-w-0">
                <div className="flex items-start gap-2">
                    {/* Dot boxed to the first line's height so it stays centered
                        on line 1 when the name wraps to two lines. */}
                    <span className="flex h-5 shrink-0 items-center md:h-6">
                        <IndicationUnit
                            variant="status"
                            state={pending ? "pending" : "success"}
                            size={7}
                        />
                    </span>
                    <Text
                        variant="small"
                        render={<span />}
                        className="min-w-0 text-foreground-base break-words"
                    >
                        {program.name}
                    </Text>
                </div>
                <Text
                    variant="fine-print"
                    className="text-foreground-muted mt-1 pl-4"
                >
                    {program.regionLabel}
                </Text>
            </div>
        </ListItem>
    );
}
